import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Trash2, Plus, GripVertical, Save, Edit2, X } from 'lucide-react';

/**
 * NestedObjectives — a compact sub-section that hangs beneath a Goal card.
 * Objectives now have BOTH a name and a description. Adds, edits, deletes
 * and reorders (HTML5 drag-and-drop) — no extra dependencies.
 */
const ACHIEVED_OPTIONS = [
  { value: '', label: 'Not Assessed' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'partial', label: 'Partial' },
];

const badge = (v) => {
  switch (v) {
    case 'yes': return 'bg-green-500/20 text-green-300 border-green-500/40';
    case 'no': return 'bg-red-500/20 text-red-300 border-red-500/40';
    case 'partial': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40';
    default: return 'bg-gray-500/20 text-gray-300 border-gray-500/40';
  }
};

const NestedObjectives = ({ goal, onAdd, onUpdate, onRemove, onReorder }) => {
  const objectives = goal.objectives || [];
  const [draftName, setDraftName] = useState('');
  const [draftDesc, setDraftDesc] = useState('');
  const [dragFrom, setDragFrom] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingDesc, setEditingDesc] = useState('');

  const submit = () => {
    if (!draftName.trim()) return;
    onAdd(draftName, draftDesc);
    setDraftName('');
    setDraftDesc('');
  };

  const startEdit = (obj) => {
    setEditingId(obj.id);
    setEditingName(obj.name || '');
    setEditingDesc(obj.description || '');
  };
  const commitEdit = () => {
    if (editingId != null) {
      onUpdate(editingId, 'name', editingName.trim());
      onUpdate(editingId, 'description', editingDesc.trim());
    }
    setEditingId(null);
    setEditingName('');
    setEditingDesc('');
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingDesc('');
  };

  return (
    <div className="mt-2 border-t border-gray-600/50 pt-3">
      <Label className="text-gray-300 text-xs uppercase tracking-wide">
        Objectives ({objectives.length})
      </Label>

      {objectives.length === 0 && (
        <p className="text-gray-500 text-xs italic mt-1">
          No objectives yet — add SMART objectives (Specific, Measurable, Attainable, Realistic, Time-sensitive) that support this goal.
        </p>
      )}

      {/* Existing objectives */}
      <ul className="mt-2 space-y-2">
        {objectives.map((obj, idx) => {
          const isEditing = editingId === obj.id;
          return (
            <li
              key={obj.id}
              draggable={!isEditing}
              onDragStart={() => setDragFrom(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragFrom != null && dragFrom !== idx) onReorder(dragFrom, idx);
                setDragFrom(null);
              }}
              onDragEnd={() => setDragFrom(null)}
              className={`flex items-start gap-2 bg-gray-800/60 border border-gray-700 rounded-md px-2 py-2 ${
                dragFrom === idx ? 'opacity-60' : ''
              }`}
              data-testid={`objective-row-${obj.id}`}
            >
              <GripVertical
                className={`h-4 w-4 mt-1 flex-shrink-0 ${isEditing ? 'text-gray-700' : 'text-gray-500 cursor-grab'}`}
                title={isEditing ? 'Finish editing to reorder' : 'Drag to reorder'}
              />
              <div className="flex-1 min-w-0 space-y-1">
                {isEditing ? (
                  <>
                    <Input
                      autoFocus
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      placeholder="Objective name (e.g. Rapid Evacuation)"
                      className="bg-gray-900 border-gray-700 text-white text-sm h-8"
                      data-testid={`objective-edit-name-${obj.id}`}
                    />
                    <Textarea
                      value={editingDesc}
                      onChange={(e) => setEditingDesc(e.target.value)}
                      placeholder="SMART description (Specific, Measurable, Attainable, Realistic, Time-sensitive) — e.g. Evacuate 500 residents within 60 minutes of alert."
                      className="bg-gray-900 border-gray-700 text-white text-sm"
                      rows={2}
                      data-testid={`objective-edit-desc-${obj.id}`}
                    />
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-white leading-snug break-words">
                      {obj.name || <span className="italic text-gray-500">(unnamed)</span>}
                    </p>
                    {obj.description && (
                      <p className="text-xs text-gray-300 leading-snug break-words">
                        {obj.description}
                      </p>
                    )}
                  </>
                )}
              </div>
              {/* Achieved dropdown */}
              {!isEditing && (
                <select
                  value={obj.achieved || ''}
                  onChange={(e) => onUpdate(obj.id, 'achieved', e.target.value || null)}
                  className={`text-xs rounded border px-2 py-1 focus:outline-none ${badge(obj.achieved)}`}
                  data-testid={`objective-achieved-${obj.id}`}
                >
                  {ACHIEVED_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={commitEdit}
                    className="h-8 border-green-600 text-green-400 hover:text-green-300"
                    data-testid={`objective-commit-${obj.id}`}
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelEdit}
                    className="h-8 border-gray-600 text-gray-300"
                    data-testid={`objective-cancel-${obj.id}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startEdit(obj)}
                    className="h-8 border-blue-600 text-blue-400 hover:text-blue-300"
                    data-testid={`objective-edit-${obj.id}`}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRemove(obj.id)}
                    className="h-8 border-red-600 text-red-400 hover:text-red-300"
                    data-testid={`objective-remove-${obj.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </li>
          );
        })}
      </ul>

      {/* Add-objective inline form */}
      <div className="mt-3 space-y-2 bg-gray-800/40 border border-gray-700/70 rounded-md p-2">
        <Input
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && draftName.trim()) submit();
          }}
          placeholder="Objective name (e.g. Rapid Evacuation)"
          className="bg-gray-900 border-gray-700 text-white text-sm h-8"
          data-testid={`objective-new-name-${goal.id}`}
        />
        <Textarea
          value={draftDesc}
          onChange={(e) => setDraftDesc(e.target.value)}
          placeholder="SMART description (Specific, Measurable, Attainable, Realistic, Time-sensitive) — e.g. Evacuate 500 residents within 60 minutes of alert."
          className="bg-gray-900 border-gray-700 text-white text-sm"
          rows={2}
          data-testid={`objective-new-desc-${goal.id}`}
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={submit}
            disabled={!draftName.trim()}
            className="h-8 bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            data-testid={`objective-add-btn-${goal.id}`}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add objective
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NestedObjectives;
